import { createFileRoute } from "@tanstack/react-router";
import React, { useMemo, useState } from "react";
import { ArrowRight, Clock, Library, TrendingUp, User } from "lucide-react";
import { Link } from "@tanstack/react-router";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import { formatTZS } from "@/utils/formatters";
import { useStudentCourses } from "@/hooks/useStudentData";
import { GridSkeleton } from "@/components/shared/Skeletons";
import EmptyState from "@/components/shared/EmptyState";
import { SearchInput } from "@/components/shared/SearchInput";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { taskpartPaidCourseTopics } from "@/data/taskpartProduct";

export const Route = createFileRoute("/student/courses/")({ component: StudentCourses });

type CourseItem = {
  id: string | number;
  title: string;
  category: string;
  instructor: string;
  duration: string;
  enrollments: number;
  fee: number;
  image: string;
  type: string;
  slug?: string;
};

function StudentCourses() {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | "free" | "orientation" | "paid">("all");
  const { data: coursesData = [], isLoading } = useStudentCourses();
  const filtered = useMemo(
    () =>
      coursesData.filter(
        (c: CourseItem) =>
          ((c.title?.toLowerCase() || "").includes(search.toLowerCase()) ||
            (c.category?.toLowerCase() || "").includes(search.toLowerCase())) &&
          (filter === "all" || c.type === filter),
      ),
    [filter, search, coursesData],
  );

  if (isLoading) {
    return <GridSkeleton cards={6} />;
  }
  const visibleCourses = filtered.slice(0, 4);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Courses and training"
        subtitle="Explore orientation, free readiness learning, and premium career courses that support the new Taskpart student journey."
        breadcrumbs={[
          { label: "Dashboard", path: "/student/dashboard" },
          { label: "Courses and Training" },
        ]}
        action={
          <Tabs value={filter} onValueChange={(v) => setFilter(v as typeof filter)}>
            <TabsList variant="segmented" aria-label="Filter courses" className="max-w-full overflow-x-auto">
              {[
                { id: "all", label: "All courses" },
                { id: "orientation", label: "Orientation" },
                { id: "free", label: "Free" },
                { id: "paid", label: "Premium" },
              ].map((item) => (
                <TabsTrigger key={item.id} value={item.id}>
                  {item.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        }
      />

      <SectionCard
        title="Discover programs"
        actions={
          <Button asChild variant="ghost" size="sm">
            <Link to="/student/courses">
              More courses
              <ArrowRight />
            </Link>
          </Button>
        }
      >
        <div className="space-y-2">
          <SearchInput
            className="sm:w-full"
            value={search}
            onChange={setSearch}
            placeholder="Search courses by skill, title, or instructor"
          />
          <p className="text-caption text-muted-foreground">
            Showing {visibleCourses.length} of {filtered.length} available programs
          </p>
          <p className="text-caption text-muted-foreground">
            Paid track examples: {taskpartPaidCourseTopics.slice(0, 3).join(", ")}.
          </p>
        </div>
      </SectionCard>

      {filtered.length === 0 ? (
        <EmptyState
          icon={<Library />}
          title="No courses found"
          description="Try another keyword for your search."
        />
      ) : (
        <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {visibleCourses.map((c: CourseItem) => (
            <li
              key={c.id}
              className="flex min-w-0 flex-col overflow-hidden rounded-md border border-border bg-card transition-colors hover:border-hairline-strong"
            >
              <div className="relative h-36 overflow-hidden border-b border-border bg-muted">
                <img src={c.image} alt={c.title} className="size-full object-cover text-[0px]" />
                <Badge variant="secondary" className="absolute top-3 right-3">
                  {c.category}
                </Badge>
              </div>
              <div className="flex flex-1 flex-col p-4">
                <h3 className="mb-3 line-clamp-2 min-h-10 text-[15px] leading-5 font-medium text-foreground">
                  {c.title}
                </h3>
                <div className="mb-4 space-y-1.5">
                  <Row icon={<User />} text={c.instructor} />
                  <Row icon={<Clock />} text={c.duration} />
                  <Row icon={<TrendingUp />} text={`${c.enrollments} enrolled`} />
                </div>
                <div className="mt-auto flex items-center justify-between gap-2 border-t border-border pt-3">
                  <div className="min-w-0">
                    <p className="eyebrow">Course fee</p>
                    <p className="font-mono text-base whitespace-nowrap text-foreground tabular-nums">
                      {c.fee === 0 ? "FREE" : formatTZS(c.fee)}
                    </p>
                  </div>
                  <Button asChild size="sm" variant="outline">
                    <Link to={`/student/courses/${c.slug || c.id}` as never}>
                      {c.type === "orientation" ? "Start" : "View"}
                    </Link>
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Row({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="flex min-w-0 items-center gap-2 text-caption text-muted-foreground">
      <span className="flex size-5 shrink-0 items-center justify-center [&_svg]:size-3.5" aria-hidden>
        {icon}
      </span>
      <span className="truncate">{text}</span>
    </div>
  );
}
